import { useCallback, useMemo } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { Workflow } from '~/lib/types/workflow-types';
import useBasket from '~/hooks/use-basket';
import { ProgressBar } from '~/components/progress-bar/progress-bar';
import { ToStepperTitle } from './to-stepper-title';
import { BASKETITEMID } from '~/lib/getCurrentItemId';
import { useAnalytics } from '@parcel2go/npm-p2g-app';

type Props = { workflow: Workflow; path: string; disableNavigation?: boolean; formId?: string };

function Stepper({ workflow, path, disableNavigation, formId }: Props) {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { currentItemId } = useBasket();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);

  const steps = useMemo(() => {
    if (!workflow) return [];
    return workflow.screens.map((step) => step.path);
  }, [workflow]);

  const translatedSteps = useMemo(() => {
    return steps.map((step) => {
      return { key: step, text: t(`STEPPER_STEP_${step.toUpperCase()}`, { defaultValue: ToStepperTitle(step) }) };
    });
  }, [steps, t]);

  const activeStep = useMemo(() => {
    return steps.findIndex((x) => x.toUpperCase() === path.toUpperCase());
  }, [path, steps]);

  const onStepClick = useCallback(
    (step: number) => {
      if (step < activeStep) {
        track({ type: 'stepper_click', payload: { step: steps[step] } });
        navigate(
          {
            pathname: `../${steps[step].toLowerCase()}`,
            search: createSearchParams({ [BASKETITEMID]: currentItemId }).toString()
          },
          {
            relative: 'path',
            replace: true
          }
        );
      }
    },
    [activeStep, track, steps, navigate, currentItemId]
  );

  return (
    <ProgressBar
      formId={formId}
      currentIndex={activeStep}
      progressBars={translatedSteps}
      onStepClick={disableNavigation ? undefined : onStepClick}
    />
  );
}

export default Stepper;
